import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import ProductFormModal from '@/pages/admin/components/menu/ProductFormModal';
import CategoryFormModal from '@/pages/admin/components/menu/CategoryFormModal';
import DeleteConfirmModal from '@/pages/admin/components/menu/DeleteConfirmModal';
import MenuImportTab from '@/pages/admin/components/menu/MenuImportTab';

interface Category {
  id: string;
  name_de: string;
  name_en: string;
  icon: string;
  sort_order: number;
  active: boolean;
}

interface Product {
  id: string;
  category_id: string;
  name_de: string;
  name_en: string;
  description_de: string;
  description_en: string;
  price: number;
  image_url: string;
  active: boolean;
  popular: boolean;
  badge_de: string | null;
  badge_en: string | null;
  sort_order: number;
}

type SubTab = 'products' | 'categories' | 'import';

const MenuTab = () => {
  const [subTab, setSubTab] = useState<SubTab>('products');
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [popularFilter, setPopularFilter] = useState<boolean>(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [copying, setCopying] = useState<string | null>(null);
  const [bulkAction, setBulkAction] = useState<'activate' | 'deactivate' | 'delete' | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Modals
  const [productModal, setProductModal] = useState<{ open: boolean; product: Product | null }>({ open: false, product: null });
  const [categoryModal, setCategoryModal] = useState<{ open: boolean; category: Category | null }>({ open: false, category: null });
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; type: 'product' | 'category'; id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [{ data: cats }, { data: prods }] = await Promise.all([
      supabase.from('categories').select('*').order('sort_order'),
      supabase.from('products').select('*').order('sort_order'),
    ]);
    if (cats) setCategories(cats);
    if (prods) setProducts(prods);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredProducts = products.filter((p) => {
    const matchCat = selectedCategory === 'all' || p.category_id === selectedCategory;
    const matchSearch = !search || p.name_de.toLowerCase().includes(search.toLowerCase()) || p.name_en.toLowerCase().includes(search.toLowerCase());
    const matchActive = activeFilter === 'all' || (activeFilter === 'active' ? p.active : !p.active);
    const matchPopular = !popularFilter || p.popular;
    return matchCat && matchSearch && matchActive && matchPopular;
  });

  const getCategoryName = (id: string) => categories.find((c) => c.id === id)?.name_de ?? '—';

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    if (deleteModal.type === 'product') {
      await supabase.from('products').update({ active: false }).eq('id', deleteModal.id);
    } else {
      await supabase.from('categories').update({ active: false }).eq('id', deleteModal.id);
    }
    setDeleting(false);
    setDeleteModal(null);
    fetchAll();
  };

  const toggleProductActive = async (product: Product) => {
    await supabase.from('products').update({ active: !product.active }).eq('id', product.id);
    fetchAll();
  };

  const toggleProductPopular = async (product: Product) => {
    await supabase.from('products').update({ popular: !product.popular }).eq('id', product.id);
    fetchAll();
  };

  const handleCopy = async (product: Product) => {
    setCopying(product.id);
    const { id: _id, ...rest } = product;
    await supabase.from('products').insert({
      ...rest,
      name_de: `${product.name_de} (Kopie)`,
      name_en: `${product.name_en} (Copy)`,
      active: false,
    });
    setCopying(null);
    fetchAll();
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filteredProducts.length) setSelected(new Set());
    else setSelected(new Set(filteredProducts.map((p) => p.id)));
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selected.size === 0) return;
    setBulkLoading(true);
    const ids = Array.from(selected);
    if (bulkAction === 'activate') {
      await supabase.from('products').update({ active: true }).in('id', ids);
    } else if (bulkAction === 'deactivate') {
      await supabase.from('products').update({ active: false }).in('id', ids);
    } else if (bulkAction === 'delete') {
      await supabase.from('products').update({ active: false }).in('id', ids);
    }
    setBulkLoading(false);
    setBulkAction(null);
    setSelected(new Set());
    fetchAll();
  };

  const activeCount = products.filter((p) => p.active).length;
  const inactiveCount = products.filter((p) => !p.active).length;
  const popularCount = products.filter((p) => p.popular).length;

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Menü Yönetimi</h2>
          <p className="text-gray-500 text-sm">
            {products.length} ürün · {categories.length} kategori ·{' '}
            <span className="text-green-600 font-medium">{activeCount} aktif</span> ·{' '}
            <span className="text-gray-400">{inactiveCount} pasif</span> ·{' '}
            <span className="text-orange-500 font-medium">{popularCount} popüler</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {subTab === 'products' && selected.size > 0 && (
            <div className="flex items-center gap-2">
              <select
                value={bulkAction ?? ''}
                onChange={(e) => setBulkAction(e.target.value as typeof bulkAction)}
                className="px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 outline-none focus:border-orange-400 cursor-pointer"
              >
                <option value="">Toplu İşlem ({selected.size})</option>
                <option value="activate">Aktif Yap</option>
                <option value="deactivate">Pasif Yap</option>
                <option value="delete">Pasife Al</option>
              </select>
              {bulkAction && (
                <button
                  onClick={handleBulkAction}
                  disabled={bulkLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  {bulkLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : null}
                  Uygula
                </button>
              )}
            </div>
          )}
          {subTab === 'products' && (
            <button
              onClick={() => setProductModal({ open: true, product: null })}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></div>
              Ürün Ekle
            </button>
          )}
          {subTab === 'categories' && (
            <button
              onClick={() => setCategoryModal({ open: true, category: null })}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></div>
              Kategori Ekle
            </button>
          )}
        </div>
      </div>

      {/* Sub tabs */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {([
          ['products', 'Ürünler', 'ri-restaurant-line'],
          ['categories', 'Kategoriler', 'ri-grid-line'],
          ['import', 'CSV İçe Aktar', 'ri-file-excel-2-line'],
        ] as const).map(([id, label, icon]) => (
          <button
            key={id}
            onClick={() => { setSubTab(id); setSelected(new Set()); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap
              ${subTab === id ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className={icon}></i></div>
            {label}
          </button>
        ))}
      </div>

      {/* Products tab */}
      {subTab === 'products' && (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-search-line text-sm"></i>
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Ürün ara..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 outline-none focus:border-orange-400 cursor-pointer"
            >
              <option value="all">Tüm Kategoriler</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name_de}</option>
              ))}
            </select>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value as typeof activeFilter)}
              className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 outline-none focus:border-orange-400 cursor-pointer"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
            </select>
            <button
              onClick={() => setPopularFilter((v) => !v)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                popularFilter ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-fire-line"></i></div>
              Popüler
            </button>
          </div>

          {/* Bulk select bar */}
          {filteredProducts.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-2 bg-white rounded-xl border border-gray-100">
              <input
                type="checkbox"
                checked={selected.size === filteredProducts.length && filteredProducts.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 accent-orange-500 cursor-pointer"
              />
              <span className="text-xs text-gray-500 font-medium">
                {selected.size > 0 ? `${selected.size} ürün seçildi` : `${filteredProducts.length} ürün gösteriliyor`}
              </span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-7 h-7 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={selected.size === filteredProducts.length && filteredProducts.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 accent-orange-500 cursor-pointer"
                      />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ürün</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Kategori</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Fiyat</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Durum</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Popüler</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className={`hover:bg-gray-50 transition-colors ${!product.active ? 'opacity-60' : ''} ${selected.has(product.id) ? 'bg-orange-50/50' : ''}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(product.id)}
                          onChange={() => toggleSelect(product.id)}
                          className="w-4 h-4 accent-orange-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {product.image_url ? (
                            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100">
                              <img src={product.image_url} alt={product.name_de} className="w-full h-full object-cover object-top" />
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <i className="ri-image-line text-gray-400"></i>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-semibold text-gray-900 leading-tight">{product.name_de}</p>
                            {product.badge_de && (
                              <span className="text-xs text-orange-500 font-medium">{product.badge_de}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{getCategoryName(product.category_id)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-bold text-gray-900">CHF {Number(product.price).toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <button
                          onClick={() => toggleProductActive(product)}
                          className={`text-xs font-semibold px-2.5 py-1 rounded-full cursor-pointer transition-all ${
                            product.active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          {product.active ? 'Aktif' : 'Pasif'}
                        </button>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <button
                          onClick={() => toggleProductPopular(product)}
                          className={`text-xs font-semibold px-2.5 py-1 rounded-full cursor-pointer transition-all ${
                            product.popular ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                        >
                          {product.popular ? 'Popüler' : 'Normal'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {/* Copy */}
                          <button
                            onClick={() => handleCopy(product)}
                            disabled={copying === product.id}
                            title="Kopyala"
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all cursor-pointer disabled:opacity-50"
                          >
                            {copying === product.id
                              ? <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                              : <i className="ri-file-copy-line text-sm"></i>
                            }
                          </button>
                          {/* Edit */}
                          <button
                            onClick={() => setProductModal({ open: true, product })}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-orange-50 text-gray-400 hover:text-orange-500 transition-all cursor-pointer"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          {/* Delete */}
                          <button
                            onClick={() => setDeleteModal({ open: true, type: 'product', id: product.id, name: product.name_de })}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredProducts.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-gray-400 text-sm">
                        Ürün bulunamadı
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Categories tab */}
      {subTab === 'categories' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Kategori</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">İngilizce</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ürün Sayısı</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Durum</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {categories.map((cat) => (
                <tr key={cat.id} className={`hover:bg-gray-50 transition-colors ${!cat.active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-orange-500`}></i>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{cat.name_de}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="text-sm text-gray-500">{cat.name_en}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-700">
                      {products.filter((p) => p.category_id === cat.id && p.active).length} aktif / {products.filter((p) => p.category_id === cat.id).length} toplam
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cat.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {cat.active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setCategoryModal({ open: true, category: cat })}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-orange-50 text-gray-400 hover:text-orange-500 transition-all cursor-pointer"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => setDeleteModal({ open: true, type: 'category', id: cat.id, name: cat.name_de })}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-400 text-sm">
                    Kategori bulunamadı
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Import tab */}
      {subTab === 'import' && (
        <MenuImportTab categories={categories} onImported={fetchAll} />
      )}

      {/* Modals */}
      {productModal.open && (
        <ProductFormModal
          product={productModal.product}
          categories={categories}
          onClose={() => setProductModal({ open: false, product: null })}
          onSaved={() => { setProductModal({ open: false, product: null }); fetchAll(); }}
        />
      )}

      {categoryModal.open && (
        <CategoryFormModal
          category={categoryModal.category}
          onClose={() => setCategoryModal({ open: false, category: null })}
          onSaved={() => { setCategoryModal({ open: false, category: null }); fetchAll(); }}
        />
      )}

      {deleteModal?.open && (
        <DeleteConfirmModal
          title={deleteModal.type === 'product' ? 'Ürünü Pasife Al' : 'Kategoriyi Pasife Al'}
          description={`"${deleteModal.name}" pasif hale getirilecek. Emin misiniz?`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteModal(null)}
          loading={deleting}
        />
      )}

      {/* Bulk action confirm */}
      {bulkAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setBulkAction(null)} />
          <div className="relative bg-white rounded-2xl w-full max-w-sm p-6 flex flex-col gap-4">
            <h3 className="text-base font-bold text-gray-900 text-center">
              {selected.size} ürüne toplu işlem
            </h3>
            <p className="text-sm text-gray-500 text-center">
              {bulkAction === 'activate' && 'Seçili ürünler aktif yapılacak.'}
              {bulkAction === 'deactivate' && 'Seçili ürünler pasif yapılacak.'}
              {bulkAction === 'delete' && 'Seçili ürünler pasife alınacak.'}
            </p>
            <div className="flex items-center gap-3">
              <button onClick={() => setBulkAction(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={handleBulkAction} disabled={bulkLoading} className="flex-1 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-2">
                {bulkLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                Uygula
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuTab;
